import AgenciesMain from '@/components/agencies/agencies-main';
import AgencyPlaceholder from '@/components/agencies/agency-placeholder';
import CreateAgencyUser from '@/components/agencies/create/create-agency-user';
import EditAgency from '@/components/agencies/edit/edit-agency';
import EditAgencyUser from '@/components/agencies/edit/edit-agency-user';
import AgencySitesList from '@/components/agencies/sites/agency-sites-list';
import SitesToAgencyListDialog from '@/components/agencies/sites/sites-to-agency-list-dialog';
import AgencyUsersList from '@/components/agencies/users/agency-users-list';
import DeleteUserDialog from '@/components/agencies/users/delete-user-dialog';
import RemoveUserFromAgencyDialog from '@/components/agencies/users/remove-user-from-agency-dialog';
import UsersToAgencyListDialog from '@/components/agencies/users/users-to-agency-list-dialog';
import AgendaMain from '@/components/agenda/agenda-main';
import AuditsList from '@/components/audits/audits-list';
import ClientInformation from '@/components/client/client-information';
import DeleteManagerPlaceDialog from '@/components/common/custom-fields/delete-manager-place-dialog';
import RedirectToMainPage from '@/components/common/redirect-to-main-page/redirect-to-main-page';
import ContractsList from '@/components/contracts/contracts-list';
import CreateContractDialog from '@/components/contracts/create/create-contract-dialog';
import DevisList from '@/components/devis/devis-list';
import DevisListGeneral from '@/components/devis/devis-list-general';
import DisordersTreatmentList from '@/components/disorders-treatment/disorders-treatment-list';
import GetTokenFromUrl from '@/components/get-token-from-url/get-token-from-url';
import ExportIntervention from '@/components/interventions/export-intervention/export-intervention';
import InterventionsList from '@/components/interventions/interventions-list';
import Layout from '@/components/layout';
import LoginPage from '@/components/login/login-page';
import Main from '@/components/main';
import MapMain from '@/components/map/map-main';
import NotAllowedPage from '@/components/not-allowed-page/not-allowed-page';
import PlaceInformation from '@/components/place/place-information';
import RefreshTokenPage from '@/components/refresh-token-page/refresh-token-page';
import EditSiteParentDialog from '@/components/site/edit-site-parent-dialog';
import SiteInformation from '@/components/site/site-information';
import AssignDialog from '@/components/users/assign-dialog';
import CompactUsersListDialog from '@/components/users/compact-users-list-dialog';
import CreateUser from '@/components/users/create/create-user';
import EditUser from '@/components/users/edit/edit-user';
import EditUserPassDialog from '@/components/users/password/edit-user-pass-dialog';
import UsersListDialog from '@/components/users/users-list-dialog';
import UsersListMain from '@/components/users/users-list-main';
import i18nProvider from '@/providers/i18nProvider';
import mainTheme from '@/themes/main-theme';
import {
    ADD_ROUTE,
    AGENCY_ROUTE,
    AGENDA_ROUTE,
    API_ENTRYPOINT_URI,
    API_ENTRYPOINT_V1,
    API_ENTRYPOINT_V2,
    API_SS,
    API_SS_URI,
    ASSIGN_CLIENT_ROUTE,
    ASSIGN_PLACE_ROUTE,
    ASSIGN_SITE_ROUTE,
    AUDITS_ROUTE,
    BIG_ADMIN,
    BIG_CLIENT_ADMIN,
    BIG_OPERATOR,
    CHANGE_PASS_ROUTE,
    CLIENT_METADATA_AVATAR_TYPE_NAME,
    CLIENT_USERS_LIST_ROUTE,
    CONTRACTS_ROUTE,
    COUNTER_SERVICE_ENTRYPOINT,
    DELETE_MANAGER_PLACE_ROUTE,
    DELETE_USER_ROUTE,
    DEVIS_ROUTE,
    DISORDERS_TREATMENT_ROUTE,
    EDIT_ROUTE,
    EDIT_SITE_PARENT_ROUTE,
    EXPORT_ROUTE,
    FILE_SYSTEM_MAPPER_ENTRYPOINT,
    FILE_SYSTEM_MAPPER_ENTRYPOINT_URI,
    GET_TOKEN_FROM_URL_ROUTE,
    INTERVENTIONS_ROUTE,
    MAP_ROUTE,
    METABASE_ENTRYPOINT,
    METABASE_ENTRYPOINT_URI,
    NAVIGATION_ROUTE,
    PLACES_METADATA_AVATAR_TYPE_NAME,
    REFRESH_TOKEN_KEY,
    REFRESH_TOKEN_URL_QUERY_PARAM,
    REMOVE_USER_FROM_AGENCY_ROUTE,
    ROLE_BIG_CREATE_CONTRACT,
    ROLE_BIG_READ_CLIENT,
    ROLE_BIG_READ_CONTRACT,
    ROLE_BIG_READ_DEMAND,
    ROLE_BIG_READ_INTERVENTION,
    ROLE_BIG_READ_QUOTE,
    ROLE_BIG_READ_SITE,
    SITE_USERS_LIST_ROUTE,
    SITES_METADATA_AVATAR_TYPE_NAME,
    SITES_ROUTE,
    TOKEN_KEY,
    TOKEN_WITHOUT_ROLES_KEY,
    USERS_ROUTE,
} from '@/utils/CONST';
import { QueryClient } from '@tanstack/react-query';
import { initializeBaseTools } from 'base-tools-360smartconnect';
import authProvider from 'base-tools-360smartconnect/providers/authProvider';
import dataProvider from 'base-tools-360smartconnect/providers/dataProvider';
import { Fragment } from 'react';
import { Admin, CustomRoutes, memoryStore } from 'react-admin';
import 'react-image-lightbox/style.css';
import { Route } from 'react-router-dom';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 10000,
        },
    },
});

initializeBaseTools({
    API_ENTRYPOINT_URI: API_ENTRYPOINT_URI,
    API_FILE_SYSTEM_MAPPER_ENTRYPOINT: FILE_SYSTEM_MAPPER_ENTRYPOINT,
    API_FILE_SYSTEM_MAPPER_ENTRYPOINT_URI: FILE_SYSTEM_MAPPER_ENTRYPOINT_URI,
});

const baseAuthProvider = authProvider({
    API_ENTRYPOINT: API_ENTRYPOINT_V1,
    API_ENTRYPOINT_URI: API_ENTRYPOINT_URI,
    REFRESH_TOKEN_KEY: REFRESH_TOKEN_KEY,
    TOKEN_WITH_ROLES_KEY: TOKEN_KEY,
    TOKEN_WITHOUT_ROLES_KEY,
});

/**
 * base-tools-360smartconnect checkAuth rejects with '' when the JWT is expired locally.
 * ra-core useCheckAuth / useAuthState pass string rejections straight to notify(); an empty
 * string yields a snackbar with no label. map to { message: false } to skip that toast (same
 * pattern as checkError for 401/403 in the package).
 */
const appAuthProvider = {
    ...baseAuthProvider,
    checkAuth: async (...args: unknown[]) => {
        try {
            await (baseAuthProvider.checkAuth as (...a: unknown[]) => Promise<void>)(...args);
        } catch (e) {
            if (e === '') {
                return Promise.reject({ message: false });
            }
            throw e;
        }
    },
};

const App = () => (
    <Admin
        dataProvider={dataProvider({
            API_ENTRYPOINT_V1,
            API_ENTRYPOINT_V2,
            API_ENTRYPOINT_URI,
            TOKEN_WITHOUT_ROLES_KEY,
            API_COUNTER_SERVICE_ENTRYPOINT: COUNTER_SERVICE_ENTRYPOINT,
            API_FILE_SYSTEM_MAPPER_ENTRYPOINT: FILE_SYSTEM_MAPPER_ENTRYPOINT,
            API_FILE_SYSTEM_MAPPER_ENTRYPOINT_URI: FILE_SYSTEM_MAPPER_ENTRYPOINT_URI,
            API_METABASE_ENTRYPOINT: METABASE_ENTRYPOINT,
            API_METABASE_ENTRYPOINT_URI: METABASE_ENTRYPOINT_URI,
            API_SS_ENTRYPOINT: API_SS,
            API_SS_ENTRYPOINT_URI: API_SS_URI,
        })}
        i18nProvider={i18nProvider}
        authProvider={appAuthProvider}
        theme={mainTheme}
        layout={Layout}
        loginPage={LoginPage}
        queryClient={queryClient}
        store={memoryStore()}
    >
        {(permissions) => (
            <Fragment>
                <CustomRoutes>
                    {/* Root redirect */}
                    <Route path={'/'} element={<RedirectToMainPage />} />

                    {/* Agenda view */}
                    <Route path={`/${AGENDA_ROUTE}`} element={<AgendaMain />} />

                    {/* Map view */}
                    <Route path={`/${MAP_ROUTE}`} element={<MapMain />} />

                    {/* Agencies view */}
                    <Route path={`/${AGENCY_ROUTE}`} element={<AgenciesMain />}>
                        <Route path={`:agencyId/${EDIT_ROUTE}/:agencyId`} element={<EditAgency />} />
                        <Route path={':agencyId'} element={<AgencyPlaceholder />}>
                            <Route path={`${ADD_ROUTE}_user_list`} element={<UsersToAgencyListDialog />} />
                            <Route path={`${ADD_ROUTE}_user`} element={<CreateAgencyUser select={true} />} />
                            <Route path={`${EDIT_ROUTE}_user/:userId`} element={<EditAgencyUser />} />
                            <Route path={`${CHANGE_PASS_ROUTE}/:userId`} element={<EditUserPassDialog />} />
                            <Route path={`${DELETE_USER_ROUTE}/:userId`} element={<DeleteUserDialog />} />
                            <Route
                                path={`${REMOVE_USER_FROM_AGENCY_ROUTE}/:userId`}
                                element={<RemoveUserFromAgencyDialog />}
                            />
                        </Route>
                        <Route path={`:agencyId/${USERS_ROUTE}`} element={<AgencyUsersList />}>
                            <Route path={`${ADD_ROUTE}_user_list`} element={<UsersToAgencyListDialog />} />
                            <Route path={`${ADD_ROUTE}_user`} element={<CreateAgencyUser select={true} />} />
                            <Route path={`${EDIT_ROUTE}_user/:userId`} element={<EditAgencyUser />} />
                            <Route path={`${CHANGE_PASS_ROUTE}/:userId`} element={<EditUserPassDialog />} />
                            <Route path={`${DELETE_USER_ROUTE}/:userId`} element={<DeleteUserDialog />} />
                            <Route
                                path={`${REMOVE_USER_FROM_AGENCY_ROUTE}/:userId`}
                                element={<RemoveUserFromAgencyDialog />}
                            />
                        </Route>
                        <Route path={`:agencyId/${SITES_ROUTE}`} element={<AgencySitesList />}>
                            <Route path={`${ADD_ROUTE}_site_list`} element={<SitesToAgencyListDialog />} />
                        </Route>
                    </Route>

                    {/* Users view */}
                    <Route path={`/${USERS_ROUTE}`} element={<UsersListMain />}>
                        <Route path={ADD_ROUTE} element={<CreateUser select={true} />} />
                        <Route path={`${EDIT_ROUTE}/:userId`} element={<EditUser select={true} />} />
                        <Route path={`${CHANGE_PASS_ROUTE}/:userId`} element={<EditUserPassDialog />} />
                        <Route
                            path={`${ASSIGN_SITE_ROUTE}/:userId`}
                            element={<AssignDialog type={SITES_METADATA_AVATAR_TYPE_NAME} />}
                        />
                        <Route
                            path={`${ASSIGN_CLIENT_ROUTE}/:userId`}
                            element={<AssignDialog type={CLIENT_METADATA_AVATAR_TYPE_NAME} />}
                        />
                        <Route
                            path={`${ASSIGN_PLACE_ROUTE}/:userId`}
                            element={<AssignDialog type={PLACES_METADATA_AVATAR_TYPE_NAME} />}
                        />
                    </Route>

                    {/* Devis view */}
                    {permissions?.includes(ROLE_BIG_READ_QUOTE) &&
                        (permissions?.includes(BIG_ADMIN) ||
                            permissions?.includes(BIG_OPERATOR) ||
                            permissions?.includes(BIG_CLIENT_ADMIN)) && (
                            <Route path={`/${DEVIS_ROUTE}`} element={<DevisListGeneral />} />
                        )}

                    {/* Navigation/Client-Site-Place hierarchy view */}
                    <Route path={`/${NAVIGATION_ROUTE}`} element={<Main />}>
                        {/* Client views */}
                        <Route
                            path={`client/:clientId`}
                            element={
                                permissions?.includes(ROLE_BIG_READ_CLIENT) ? <ClientInformation /> : <NotAllowedPage />
                            }
                        />
                        <Route path={`${CLIENT_USERS_LIST_ROUTE}/:clientId`} element={<UsersListDialog />}>
                            <Route path={ADD_ROUTE} element={<CompactUsersListDialog />} />
                            <Route
                                path={`${ADD_ROUTE}/client/:clientId`}
                                element={<CreateUser select={true} contextType='client' />}
                            />
                            <Route path={`${DELETE_MANAGER_PLACE_ROUTE}/:id`} element={<DeleteManagerPlaceDialog />} />
                        </Route>

                        {/* Site views */}
                        <Route
                            path={`${EDIT_SITE_PARENT_ROUTE}/:clientId/:siteId`}
                            element={<EditSiteParentDialog />}
                        />
                        <Route
                            path={`client/:clientId/site/:siteId`}
                            element={
                                permissions?.includes(ROLE_BIG_READ_SITE) ? <SiteInformation /> : <NotAllowedPage />
                            }
                        />
                        <Route path={`${SITE_USERS_LIST_ROUTE}/:siteId`} element={<UsersListDialog />}>
                            <Route path={ADD_ROUTE} element={<CompactUsersListDialog />} />
                            <Route
                                path={`${ADD_ROUTE}/site/:siteId`}
                                element={<CreateUser select={true} contextType='site' />}
                            />
                            <Route path={`${DELETE_MANAGER_PLACE_ROUTE}/:id`} element={<DeleteManagerPlaceDialog />} />
                        </Route>

                        {/* Place views */}
                        <Route path={`client/:clientId/site/:siteId/place/:placeId`} element={<PlaceInformation />} />

                        {/* Place-related views: Interventions */}
                        <Route
                            path={`client/:clientId/site/:siteId/place/:placeId/${INTERVENTIONS_ROUTE}`}
                            element={
                                permissions?.includes(ROLE_BIG_READ_INTERVENTION) ? (
                                    <InterventionsList />
                                ) : (
                                    <RedirectToMainPage />
                                )
                            }
                        />

                        {/* Place-related views: Contracts */}
                        {permissions?.includes(ROLE_BIG_READ_CONTRACT) && (
                            <Route
                                path={`client/:clientId/site/:siteId/place/:placeId/${CONTRACTS_ROUTE}`}
                                element={<ContractsList />}
                            >
                                {permissions?.includes(ROLE_BIG_CREATE_CONTRACT) && (
                                    <Route path={ADD_ROUTE} element={<CreateContractDialog />} />
                                )}
                            </Route>
                        )}

                        {/* Place-related views: Audits */}
                        <Route
                            path={`client/:clientId/site/:siteId/place/:placeId/${AUDITS_ROUTE}`}
                            element={<AuditsList />}
                        />

                        {/* Place-related views: Disorders Treatment */}
                        <Route
                            path={`client/:clientId/site/:siteId/place/:placeId/${DISORDERS_TREATMENT_ROUTE}`}
                            element={
                                permissions?.includes(ROLE_BIG_READ_DEMAND) ? (
                                    <DisordersTreatmentList />
                                ) : (
                                    <RedirectToMainPage />
                                )
                            }
                        />

                        {/* Place-related views: Devis */}
                        <Route
                            path={`client/:clientId/site/:siteId/place/:placeId/${DEVIS_ROUTE}`}
                            element={<DevisList />}
                        />
                    </Route>
                </CustomRoutes>

                {/* No layout routes */}
                <CustomRoutes noLayout>
                    <Route path={`/c/:${REFRESH_TOKEN_URL_QUERY_PARAM}`} element={<RefreshTokenPage />} />
                    <Route path={`${GET_TOKEN_FROM_URL_ROUTE}/:refreshToken`} element={<GetTokenFromUrl />} />
                    <Route
                        path={`${EXPORT_ROUTE}/client/:clientId/site/:siteId/place/:placeId/:intId`}
                        element={<ExportIntervention />}
                    />
                </CustomRoutes>
            </Fragment>
        )}
    </Admin>
);

export default App;
